'use client';

import { FC, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField } from '@/components/common/FormComponents';
import { FormLabel } from '@/components/common/FormLabel';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { DataPackageDto } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createSimulationDatasetSaveValidation } from '@/lib/validation/saveValidationBuilders';
import { getProblemDetail } from '@/lib/utils/apiErrors';

interface AddSimulationDatasetDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onSubmit?: (data: DataPackageDto) => void;
}

export const AddSimulationDatasetDialog: FC<AddSimulationDatasetDialogProps> = ({
  open,
  onClose,
  projectId,
  onSubmit,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [doi, setDoi] = useState('');
  const [publicationDoi, setPublicationDoi] = useState('');
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  // Error states
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  // Reset form to defaults when dialog closes
  const handleClose = () => {
    setTitle('');
    setDescription('');
    setDoi('');
    setPublicationDoi('');
    setTitleError(false);
    setDescriptionError(false);
    onClose();
  };

  const isValidDoi = (doi: string): boolean => {
    // Basic DOI validation regex
    const doiRegex = /^10\.\d{4,}(?:\.\d+)*\/\S+(?:\?\S+)?$/;
    return doiRegex.test(doi);
  };

  const handleSubmit = async () => {
    const saveValidation = validateRequiredToSave(createSimulationDatasetSaveValidation({ label: title, description }));
    setTitleError(!!saveValidation.fieldErrors.label);
    setDescriptionError(!!saveValidation.fieldErrors.description);

    if (doi && !isValidDoi(doi)) {
      showSnackbar('Invalid DOI format.', 'error');
      return;
    }

    if (publicationDoi && !isValidDoi(publicationDoi)) {
      showSnackbar('Invalid publication DOI format.', 'error');
      return;
    }

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    try {
      const payload = {
        label: title.trim(),
        description: description.trim(),
        doi: doi.trim() || undefined,
        publicationDoi: publicationDoi.trim() || undefined,
        projectId: parseInt(projectId),
      };

      const savedDataset = await backend.data.create(payload);
      log.debug('[AddSimulationDatasetDialog] Saved simulation dataset:', savedDataset);

      onSubmit?.(savedDataset);
      showSnackbar('Simulation dataset created successfully.', 'success');
      handleClose();
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create simulation dataset.', 'error');
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Add Simulation Dataset"
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Add Simulation Dataset"
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      {/* Title */}
      <FormTextField
        label="Title"
        required
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          markAsInteracted();
        }}
        size="small"
        error={titleError}
        fullWidth
      />

      <Divider />

      {/* Description */}
      <Box width="100%">
        <FormLabel required>Description</FormLabel>
        <Box width="100%">
          <RichTextEditor
            value={description}
            onChange={(value) => {
              setDescription(value);
              markAsInteracted();
            }}
          />
        </Box>
        {descriptionError ? (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            Description is required
          </Typography>
        ) : null}
      </Box>

      <Divider />

      {/* DOI and Publication DOI */}
      <Box display="flex" gap={2}>
        <FormTextField
          label="DOI"
          value={doi}
          onChange={(e) => {
            setDoi(e.target.value);
            markAsInteracted();
          }}
          size="small"
          fullWidth
          helperText="Optional: Digital Object Identifier"
        />
        <FormTextField
          label="Publication DOI"
          value={publicationDoi}
          onChange={(e) => {
            setPublicationDoi(e.target.value);
            markAsInteracted();
          }}
          size="small"
          fullWidth
          helperText="Optional: Publication DOI"
        />
      </Box>
    </StandardDialog>
  );
};
